import { useCallback, useMemo, useRef } from 'react';
import { type VirtualizerHandle, VList } from 'virtua';

import {
  AutoScrollTrigger,
  type AutoScrollTriggerHandle,
} from '@/components/ui/AutoScrollTrigger';
import { FindOverlay } from '@/components/ui/FindOverlay';
import { ScrollRestoration } from '@/components/ui/ScrollRestoration';
import { searchItems, useVirtualListFind } from '@/hooks/useVirtualListFind';
import { toCaseInsensitiveSearchText } from '@/lib/unicode-search';
import { classList } from '@/utils/cn';

import { useTranscriptTermCorrection } from '../meetings-transcript-correction';
import { TranscriptTermCorrectionPopover } from '../meetings-transcript-correction.components';
import { JumpToLivePill } from '../meetings-transcript-display.components';
import {
  useAudioEnergyPulse,
  useJumpToLive,
  type useProcessedTranscripts,
} from '../meetings-transcript-display.hooks';
import {
  getSpeakerDisplay,
  joinSegmentText,
} from '../meetings-transcript.utils';
import { meetingStore } from '../meetings.state';

import { transcriptFindTunnel } from './transcript-find-tunnel';
import { TranscriptBulkActions } from './TranscriptBulkActions';
import { TranscriptRow } from './TranscriptRow';
import { useTranscriptNavigation } from './use-transcript-navigation';
import { useTranscriptSelection } from './use-transcript-selection';

import type { CalendarEventAttendee } from '../meetings.types';

interface TranscriptsProps {
  id: string;
  transcript: string;
  transcripts: ReturnType<typeof useProcessedTranscripts>;
  canDeleteChunks: boolean;
  isInPopup: boolean;
  autoScroll?: boolean;
  isRecording?: boolean;
  attendees?: CalendarEventAttendee[] | null;
  canEditSpeakers?: boolean;
  onSelectionCleared: (sourceLineIndex: number | undefined) => void;
}

export const Transcripts = ({
  id,
  transcript,
  isInPopup,
  transcripts,
  canDeleteChunks,
  autoScroll = false,
  isRecording = false,
  attendees,
  canEditSpeakers = false,
  onSelectionCleared,
}: TranscriptsProps) => {
  const selection = useTranscriptSelection({
    meetingId: id,
    transcript,
    transcripts,
    canDelete: canDeleteChunks,
    isRecording,
    onSelectionCleared: () => onSelectionCleared(activeKey),
  });
  const correction = useTranscriptTermCorrection({
    meetingId: id,
    transcript,
    canCorrect: canDeleteChunks && !isRecording && !selection.isDeleting,
  });
  const itemIdPrefix = isInPopup ? 'transcript-popup' : 'transcript-tab';
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollTriggerRef = useRef<AutoScrollTriggerHandle>(null);
  const vlistRef = useRef<VirtualizerHandle>(null);
  const navigation = useTranscriptNavigation(
    transcripts,
    selection.selectableKeys,
    selection.selectChunk,
    containerRef,
    vlistRef
  );
  const { activeKey, activeIndex, pendingFocus, setFocusedKey } = navigation;
  const energyPulseRef = useAudioEnergyPulse(
    meetingStore.subscribeAudioEnergyLevel
  );
  const { isScrollPaused, handlePausedChange, isLineNew, newLineCount } =
    useJumpToLive(transcripts.length);
  const speakerLabels = useMemo(
    () => [
      ...new Set(
        transcripts
          .map((chunk) => chunk.speaker)
          .filter((speaker): speaker is string => !!speaker)
      ),
    ],
    [transcripts]
  );
  const searchableSentences = useMemo(
    () => transcripts.map((item) => joinSegmentText(item.segments)),
    [transcripts]
  );
  const normalizedSentences = useMemo(
    () =>
      searchableSentences.map((sentence) =>
        toCaseInsensitiveSearchText(sentence)
      ),
    [searchableSentences]
  );

  const search = useCallback(
    (query: string, caseSensitive: boolean) =>
      searchItems(
        {
          length: transcripts.length,
          getText: (i) => searchableSentences[i] ?? '',
          getNormalizedText: (i) => normalizedSentences[i] ?? '',
        },
        query,
        caseSensitive
      ),
    [transcripts, searchableSentences, normalizedSentences]
  );

  const find = useVirtualListFind({
    vlistRef,
    containerRef,
    search,
    itemIdPrefix,
  });

  const handleScrollMask = (scrollTop: number) => {
    if (isInPopup) return;
    const shouldShowMask = scrollTop > 10;
    const e = containerRef.current;
    if (e) {
      e.classList.toggle('mask-linear', shouldShowMask);
      e.classList.toggle('mask-from-0', shouldShowMask);
      e.classList.toggle('mask-via-100', shouldShowMask);
      e.classList.toggle('mask-to-100', shouldShowMask);
      e.classList.toggle('mask-point-via-[10px]', shouldShowMask);
    }
  };

  const handleScroll = (scrollTop: number) => {
    handleScrollMask(scrollTop);
    find.handleScroll();
  };

  const handleOnAutoScroll = () => {
    if (autoScroll && !selection.selectedCount)
      autoScrollTriggerRef.current?.autoScroll();
  };

  return (
    <div
      className={classList(
        'relative h-full',
        !isInPopup && [
          'mask-linear mask-from-0 mask-via-100 mask-to-100',
          'mask-point-via-[10px]',
        ],
        isInPopup && ['border-t border-border-strong pb-3']
      )}
      ref={containerRef}
      onKeyDown={(event) => {
        if (
          event.key === 'Escape' &&
          selection.selectedCount &&
          !selection.isDeleting
        ) {
          event.preventDefault();
          event.stopPropagation();
          selection.clearSelection();
        }
      }}
    >
      {isInPopup ? (
        <div data-find-exclude>
          <FindOverlay
            isOpen={find.isOpen}
            query={find.query}
            onQueryChange={find.setQuery}
            caseSensitive={find.caseSensitive}
            onCaseSensitiveChange={find.setCaseSensitive}
            currentMatch={find.currentMatch}
            totalMatches={find.totalMatches}
            onNext={find.goToNext}
            onPrevious={find.goToPrevious}
            onClose={find.close}
          />
        </div>
      ) : (
        <transcriptFindTunnel.In>
          <FindOverlay
            isOpen={find.isOpen}
            query={find.query}
            onQueryChange={find.setQuery}
            caseSensitive={find.caseSensitive}
            onCaseSensitiveChange={find.setCaseSensitive}
            currentMatch={find.currentMatch}
            totalMatches={find.totalMatches}
            onNext={find.goToNext}
            onPrevious={find.goToPrevious}
            onClose={find.close}
          />
        </transcriptFindTunnel.In>
      )}
      <div
        role="listbox"
        aria-label="Transcript"
        aria-multiselectable="true"
        className="h-full"
      >
        <VList
          className={classList(
            'h-full',
            'overflow-y-auto scrollbar-hover',
            'mask-linear mask-from-100 mask-via-100 mask-to-0',
            'mask-point-via-[98%]',
            isInPopup && ['pt-2'],
            !isInPopup && ['pt-1.5 pb-6']
          )}
          onScroll={handleScroll}
          keepMounted={
            activeIndex >= 0
              ? [activeIndex, transcripts.length]
              : [transcripts.length]
          }
          ref={vlistRef}
        >
          {transcripts.map((chunk, index) => {
            const key = chunk.sourceLineIndex;
            const { isSpeakerChange, showSpeaker } = getSpeakerDisplay(
              transcripts,
              index
            );
            const isLive = isRecording && index === transcripts.length - 1;
            const isSelectable = selection.selectableKeys.has(key);
            return (
              <TranscriptRow
                key={key}
                chunk={chunk}
                index={index}
                totalCount={transcripts.length}
                itemIdPrefix={itemIdPrefix}
                isSpeakerChange={isSpeakerChange}
                showSpeaker={showSpeaker}
                isInPopup={isInPopup}
                isNewLine={isRecording && isLineNew(index)}
                isLive={isLive}
                isSelectable={isSelectable}
                isActive={key === activeKey}
                isSelected={selection.selectedKeys.has(key)}
                isDeleting={selection.isDeleting}
                meetingId={id}
                attendees={attendees}
                speakerLabels={speakerLabels}
                canEditSpeakers={canEditSpeakers}
                bubbleRef={(element) => {
                  if (isLive) energyPulseRef.current = element;
                  if (element && pendingFocus.current === key) {
                    element.focus();
                    pendingFocus.current = undefined;
                  }
                }}
                onMouseDown={(event) => {
                  if (event.detail === 1) selection.rememberPointerSelection();
                  if (isSelectable && event.shiftKey) event.preventDefault();
                }}
                onClick={(event) => {
                  if (
                    event.detail > 1 ||
                    window.getSelection()?.isCollapsed === false
                  )
                    return;
                  selection.selectChunk(key, event);
                  if (isSelectable) setFocusedKey(key);
                }}
                onDoubleClick={selection.restorePointerSelection}
                onKeyDown={(event) => navigation.handleKeyDown(key, event)}
                onSelectWord={
                  correction.isEnabled ? correction.selectWord : undefined
                }
              />
            );
          })}
          <div key="footer">
            {selection.selectedCount > 0 && <div className="h-14" />}
            <div
              style={{ height: 'calc(var(--chat-dock-inset, 0px) - 3rem)' }}
            />
            <ScrollRestoration
              id={`scroll-restore-${id}-${isInPopup ? 'popup' : 'tab'}`}
              onRestoreScroll={(offset) => vlistRef.current?.scrollTo(offset)}
            />
            {autoScroll && (
              <AutoScrollTrigger
                id={`transcripts-${id}-${isInPopup ? 'popup' : 'tab'}`}
                ref={autoScrollTriggerRef}
                sensitivity={28}
                onShouldScroll={handleOnAutoScroll}
                onPausedChange={handlePausedChange}
                smoothFollow
              />
            )}
          </div>
        </VList>
      </div>
      <JumpToLivePill
        show={autoScroll && isScrollPaused && !selection.selectedCount}
        newLineCount={newLineCount}
        onClick={() => autoScrollTriggerRef.current?.resume()}
      />
      <TranscriptBulkActions
        selection={selection}
        canDelete={canDeleteChunks}
      />
      <TranscriptTermCorrectionPopover controller={correction} />
    </div>
  );
};
